import { ActionCreatorWithPayload, AnyAction, Dispatch } from '@reduxjs/toolkit'
import { getUserRef } from 'config/database/refs'
import { DB_REF, DbLessonStat, WeaknessDetectingStats, WeaknessStat } from 'interface/database'
import {
  setCw1_50,
  setCw51_100,
  setCw101_150,
  setCw151_200,
  setCw201_250,
  setCw251_300,
  setCw301_400,
  setCw401_500,
  setCw501_600,
  setCw601_700,
  setCw701_800,
  setCw801_900,
  setCw901_1000,
  setLowercase,
  setNg4,
  setNgBi,
  setNgTri,
  setNum,
  setPunctuation,
  setStats,
  setUppercase,
} from 'store/slices/stat'

import { convertFirebaseData } from './convert-firebase-data'

export type StatTypes = WeaknessStat | DbLessonStat[] | null

type ActionCreatorType = ActionCreatorWithPayload<StatTypes>

// Keep adding properties if new one has been added in the database.
export interface StatsProps extends WeaknessDetectingStats {
  stats: DbLessonStat[]
}

const actionCreators: Partial<
  Record<
    DB_REF,
    // Typescript 'any' is okay to be used here since we are looping through all the action creators of Stats
    ActionCreatorType
  >
> = {
  [DB_REF.stats]: setStats as ActionCreatorType,
  [DB_REF.uppercase]: setUppercase as ActionCreatorType,
  [DB_REF.lowercase]: setLowercase as ActionCreatorType,
  [DB_REF.num]: setNum as ActionCreatorType,
  [DB_REF.punctuation]: setPunctuation as ActionCreatorType,
  [DB_REF.ngBi]: setNgBi as ActionCreatorType,
  [DB_REF.ngTri]: setNgTri as ActionCreatorType,
  [DB_REF.ng4]: setNg4 as ActionCreatorType,
  [DB_REF.cw1_50]: setCw1_50 as ActionCreatorType,
  [DB_REF.cw51_100]: setCw51_100 as ActionCreatorType,
  [DB_REF.cw101_150]: setCw101_150 as ActionCreatorType,
  [DB_REF.cw151_200]: setCw151_200 as ActionCreatorType,
  [DB_REF.cw201_250]: setCw201_250 as ActionCreatorType,
  [DB_REF.cw251_300]: setCw251_300 as ActionCreatorType,
  [DB_REF.cw301_400]: setCw301_400 as ActionCreatorType,
  [DB_REF.cw401_500]: setCw401_500 as ActionCreatorType,
  [DB_REF.cw501_600]: setCw501_600 as ActionCreatorType,
  [DB_REF.cw601_700]: setCw601_700 as ActionCreatorType,
  [DB_REF.cw701_800]: setCw701_800 as ActionCreatorType,
  [DB_REF.cw801_900]: setCw801_900 as ActionCreatorType,
  [DB_REF.cw901_1000]: setCw901_1000 as ActionCreatorType,
}

/**
 * This utils syncs up the redux store with firebase store for the properties of `actionCreators` object.
 * @param dispatch
 * @param uid
 */
export const loadStats = async (dispatch: Dispatch<AnyAction>, uid: string) => {
  const fireDb = await import('firebase/database')

  const { getDatabase, onValue, ref } = fireDb

  const db = getDatabase()
  const statsProps = Object.keys(actionCreators) as Array<keyof typeof actionCreators>

  statsProps.forEach((property) => {
    const propertyRef = ref(db, getUserRef(uid, { dbRef: property }))
    onValue(
      propertyRef,
      (snapshot) => {
        const actionCreator = actionCreators[property]
        if (!actionCreator) return

        const dbValue = convertFirebaseData(snapshot.val(), property)

        dispatch(actionCreator(dbValue as StatTypes))
      },
      (error) => {
        console.error('Error reading Firebase data in loadStats: ', error)
      }
    )
  })
}
